import {
  RENEWAL_DISCOUNT_APPLICATION_STATUS,
  RENEWAL_DISCOUNT_APPLICATION_STATUSES,
  RENEWAL_DISCOUNT_PREVIEW_STATUS,
  formatParisDate,
  formatParisDateTime,
  parseParisDayKey,
  type OperationDetail,
  type RenewalDiscountApplicationStatus,
  type StatusRegistry,
  type RenewalDiscountCustomerPreview,
  type RenewalDiscountDetail,
  type RenewalDiscountPreview,
} from '@flux/shared';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { AlertTriangle, Calculator, Loader2, Search } from 'lucide-react';
import { useState, type FormEvent, type JSX } from 'react';
import { PageSection } from '@/components/layout/page.js';
import { StatusBadge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { DescriptionItem, DescriptionList } from '@/components/ui/description-list.js';
import { Field } from '@/components/ui/field.js';
import { Input } from '@/components/ui/input.js';
import { Pagination } from '@/components/ui/pagination.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table.js';
import { ApiError } from '@/lib/api/client.js';
import { formatCount, formatCountLabel, formatEuros } from '../subscriptions/money.js';
import { previewRenewalCustomer } from './api.js';
import { isPreviewStale, lastPreviewRun } from './renewal-discount-activate-dialog.js';

const ROWS_PER_PAGE = 50;

function day(value: string | null): string {
  return value === null || value === '' ? '—' : formatParisDate(parseParisDayKey(value));
}

function euros(value: string | null): string {
  return value === null ? '—' : (formatEuros(value) ?? '—');
}

function Dash(): JSX.Element {
  return <span className="text-muted-foreground">—</span>;
}

/** Compteurs par statut, dans l'ordre du registre, statuts vides omis. */
export function StatusCounts({
  counts,
  registry = RENEWAL_DISCOUNT_APPLICATION_STATUS,
}: {
  counts: Partial<Record<string, number>>;
  /** Aperçu : libellés prévisionnels (`RENEWAL_DISCOUNT_PREVIEW_STATUS`) ; Suivi : statuts réels. */
  registry?: StatusRegistry<RenewalDiscountApplicationStatus>;
}): JSX.Element {
  const entries = RENEWAL_DISCOUNT_APPLICATION_STATUSES.filter((status) => (counts[status] ?? 0) > 0);
  if (entries.length === 0) {
    return <p className="text-sm text-muted-foreground">Aucun client pour l’instant.</p>;
  }
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-2">
      {entries.map((status) => (
        <li key={status} className="flex items-center gap-1.5 text-sm">
          <StatusBadge registry={registry} code={status} />
          <span className="tabular-nums">{formatCount(counts[status] ?? 0)}</span>
        </li>
      ))}
    </ul>
  );
}

/** Page du tableau d'aperçu, portée par l'URL (`?previewPage=`, `routes/operation-detail.tsx`). */
function usePreviewPage(): [number, (page: number) => void] {
  const rawSearch: unknown = useSearch({ strict: false });
  const navigate = useNavigate();
  const raw = typeof rawSearch === 'object' && rawSearch !== null ? Number((rawSearch as Record<string, unknown>).previewPage) : NaN;
  const page = Number.isInteger(raw) && raw > 0 ? raw : 1;
  function setPage(next: number): void {
    void navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({ ...prev, previewPage: next === 1 ? undefined : next }),
      replace: true,
    });
  }
  return [page, setPage];
}

function PreviewResult({ preview }: { preview: RenewalDiscountPreview }): JSX.Element {
  const [page, setPage] = usePreviewPage();
  const totalPages = Math.max(1, Math.ceil(preview.rows.length / ROWS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const rows = preview.rows.slice((currentPage - 1) * ROWS_PER_PAGE, currentPage * ROWS_PER_PAGE);
  return (
    <div className="flex flex-col gap-5">
      <DescriptionList>
        <DescriptionItem label="Renouvellements déjà prévus">
          <span className="tabular-nums">{formatCountLabel(preview.customersWithCharge, 'client', 'clients')}</span>
        </DescriptionItem>
        <DescriptionItem label="Montant estimé">
          <span className="tabular-nums">
            {euros(preview.totalDiscount)} de remise sur {euros(preview.totalBefore)}
          </span>
        </DescriptionItem>
        <DescriptionItem label="Renouvellements à venir">
          <span className="flex flex-col gap-0.5">
            <span className="tabular-nums">{formatCountLabel(preview.notGeneratedCustomers, 'client', 'clients')}</span>
            <span className="text-xs leading-5 text-muted-foreground">
              Leur renouvellement dans la période n’existe pas encore chez Recharge, qui ne génère que la prochaine charge de
              chaque abonnement. Ils seront traités au fil de l’eau : à la création de la charge (webhook charge/created) et
              par la réconciliation quotidienne.
            </span>
          </span>
        </DescriptionItem>
      </DescriptionList>

      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold">Résultat par client</h3>
        <StatusCounts counts={preview.byStatus} registry={RENEWAL_DISCOUNT_PREVIEW_STATUS} />
      </div>

      {preview.byTier.length > 0 ? (
        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">Par palier</h3>
          <ul className="flex flex-col gap-0.5 text-sm tabular-nums">
            {preview.byTier.map((tier) => (
              <li key={tier.minProducts}>
                {`${formatCountLabel(tier.minProducts, 'produit', 'produits')} ou plus · ${String(tier.percent).replace('.', ',')} % : ${formatCountLabel(tier.customers, 'client', 'clients')}`}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {preview.rows.length > 0 ? (
        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">
            {preview.rows.length < preview.customersWithCharge
              ? `Les ${String(preview.rows.length)} premiers renouvellements`
              : 'Renouvellements'}
          </h3>
          <div className="overflow-x-auto rounded-md border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Renouvellement</TableHead>
                  <TableHead className="text-right">Produits</TableHead>
                  <TableHead className="text-right">Avant</TableHead>
                  <TableHead className="text-right">Remise</TableHead>
                  <TableHead>Résultat</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.customerId}>
                    <TableCell className="max-w-56 truncate" title={row.email ?? row.customerId} translate="no">
                      {row.email ?? row.customerId}
                    </TableCell>
                    <TableCell className="whitespace-nowrap tabular-nums">{day(row.scheduledAt)}</TableCell>
                    <TableCell className="text-right tabular-nums">{row.productCount}</TableCell>
                    <TableCell className="text-right tabular-nums">{euros(row.amountBefore)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {row.discountAmount === null ? <Dash /> : euros(row.discountAmount)}
                      {row.percent === null || row.discountAmount === null ? null : (
                        <span className="ml-1 text-xs text-muted-foreground">{`(palier ${String(row.percent).replace('.', ',')} %)`}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="flex flex-wrap items-center gap-1.5">
                        <StatusBadge registry={RENEWAL_DISCOUNT_PREVIEW_STATUS} code={row.status} />
                        {row.existingCode === null ? null : <span className="font-mono text-xs" translate="no">{row.existingCode}</span>}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {totalPages > 1 ? (
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
              <span className="tabular-nums">
                {`Lignes ${formatCount((currentPage - 1) * ROWS_PER_PAGE + 1)} à ${formatCount(
                  Math.min(currentPage * ROWS_PER_PAGE, preview.rows.length),
                )} sur ${formatCount(preview.rows.length)}`}
              </span>
              <Pagination page={currentPage} totalPages={totalPages} onPageChange={setPage} />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function CustomerPreviewResult({ result }: { result: RenewalDiscountCustomerPreview }): JSX.Element {
  const { decision } = result;
  return (
    <div className="flex flex-col gap-4" data-testid="renewal-customer-preview">
      <DescriptionList>
        <DescriptionItem label="Client">
          <span translate="no">{result.email ?? '—'}</span>{' '}
          <span className="text-muted-foreground" translate="no">
            ({result.customerId})
          </span>
        </DescriptionItem>
        <DescriptionItem label="Abonné à l’activation">{result.existingSubscriber ? 'Oui' : 'Non (nouvel abonné)'}</DescriptionItem>
        <DescriptionItem label="Décision">
          <span className="flex flex-col gap-1">
            {decision.status === null ? null : (
              <span>
                <StatusBadge registry={RENEWAL_DISCOUNT_PREVIEW_STATUS} code={decision.status} />
              </span>
            )}
            <span>{decision.reason}</span>
          </span>
        </DescriptionItem>
        {decision.amountBefore === null ? null : (
          <>
            <DescriptionItem label="Produits">
              <span className="tabular-nums">
                {decision.productCount}
                {decision.percent === null ? ' (sous le premier palier)' : ` · palier ${String(decision.percent).replace('.', ',')} %`}
              </span>
            </DescriptionItem>
            <DescriptionItem label="Prix actuel">
              <span className="tabular-nums">{euros(decision.amountBefore)}</span>
            </DescriptionItem>
            {decision.targetAmount === null ? null : (
              <DescriptionItem label="Prix visé">
                <span className="tabular-nums">{euros(decision.targetAmount)}</span>
              </DescriptionItem>
            )}
            <DescriptionItem label="Remise">
              <span className="tabular-nums">{euros(decision.discountAmount)}</span>
            </DescriptionItem>
            <DescriptionItem label="Prix après remise">
              <span className="font-medium tabular-nums">{euros(decision.amountAfter)}</span>
            </DescriptionItem>
          </>
        )}
        {result.projectedDate === null ? null : (
          <DescriptionItem label="Renouvellement à venir">
            <span className="tabular-nums">
              Prévu le {day(result.projectedDate)}, pas encore généré chez Recharge : il sera traité à sa création.
            </span>
          </DescriptionItem>
        )}
      </DescriptionList>

      {result.charges.length === 0 ? (
        <p className="text-sm text-muted-foreground">Aucun renouvellement en attente chez Recharge.</p>
      ) : (
        result.charges.map((charge) => (
          <div key={charge.chargeId} className="flex flex-col gap-2 rounded-md border border-border p-3">
            <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
              <span className="font-medium tabular-nums">Renouvellement du {day(charge.scheduledAt)}</span>
              <span className="text-xs text-muted-foreground">
                charge <span translate="no">{charge.chargeId}</span>
              </span>
              <span className="text-xs text-muted-foreground">{charge.inPeriod ? '· dans la période' : '· hors période'}</span>
              {charge.chargeId === decision.chargeId ? <span className="text-xs font-medium text-primary">· retenu</span> : null}
              {charge.existingCode === null ? null : (
                <span className="text-xs">
                  · code{' '}
                  <span className="font-mono" translate="no">
                    {charge.existingCode}
                  </span>
                </span>
              )}
            </p>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Produit</TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead className="text-right">Qté</TableHead>
                    <TableHead className="text-right">Prix actuel</TableHead>
                    <TableHead className="text-right">Prix Shopify</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {charge.lines.map((line, index) => (
                    <TableRow key={`${charge.chargeId}-${String(index)}`} className="hover:bg-transparent">
                      <TableCell>
                        <span className="flex flex-col gap-0.5">
                          <span>{line.title}</span>
                          {line.bundleId === null && !line.legacy ? null : (
                            <span className="text-xs text-muted-foreground">
                              {[line.bundleId === null ? null : `Bundle ${line.bundleId}`, line.legacy ? 'Abonnement legacy' : null]
                                .filter((part) => part !== null)
                                .join(' · ')}
                            </span>
                          )}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono text-xs" translate="no">
                        {line.sku ?? '—'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{line.quantity}</TableCell>
                      <TableCell className="text-right tabular-nums">{euros(line.unitPrice)}</TableCell>
                      <TableCell className="text-right tabular-nums">{euros(line.shopifyUnitPrice)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function CustomerPreview({ operationId }: { operationId: string }): JSX.Element {
  const [query, setQuery] = useState('');
  const [queryError, setQueryError] = useState<string | undefined>(undefined);
  const mutation = useMutation({
    mutationFn: (value: string) => previewRenewalCustomer(operationId, value),
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const trimmed = query.trim();
    if (trimmed === '') {
      setQueryError('Saisissez un email ou un identifiant client.');
      document.getElementById('renewal-customer-query')?.focus();
      return;
    }
    mutation.mutate(trimmed);
  }

  return (
    <PageSection
      title="Aperçu d’un client"
      description="L’effet de l’opération sur le prochain renouvellement d’un client, ligne par ligne. Rien n’est écrit chez Recharge."
    >
      <div className="flex flex-col gap-4">
        <form className="flex flex-wrap items-end gap-2" onSubmit={handleSubmit} noValidate>
          <Field
            label="Email ou identifiant client Recharge"
            htmlFor="renewal-customer-query"
            className="w-full max-w-sm"
            error={queryError}
          >
            <Input
              id="renewal-customer-query"
              autoComplete="off"
              spellCheck={false}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setQueryError(undefined);
                // Le résultat affiché ne correspond plus à la saisie.
                if (!mutation.isPending) {
                  mutation.reset();
                }
              }}
            />
          </Field>
          <Button type="submit" variant="outline" isLoading={mutation.isPending}>
            <Search aria-hidden="true" />
            Voir l’aperçu
          </Button>
        </form>
        {mutation.isError ? (
          <p role="alert" className="text-sm text-status-red-fg">
            {mutation.error instanceof ApiError ? mutation.error.message : 'Aperçu impossible. Réessayez.'}
          </p>
        ) : null}
        {mutation.data ? <CustomerPreviewResult result={mutation.data} /> : null}
      </div>
    </PageSection>
  );
}

export interface RenewalPreviewTabProps {
  detail: OperationDetail;
  config: RenewalDiscountDetail;
  onStart: () => void;
  isStarting: boolean;
  /** Échec du lancement du calcul, affiché dans la section. */
  startError?: string | null | undefined;
}

/** Onglet « Aperçu » (`07` §6.2) : décision qu'aurait chaque client, et aperçu d'un client. */
export function RenewalPreviewTab({ detail, config, onStart, isStarting, startError = null }: RenewalPreviewTabProps): JSX.Element {
  const latest = config.lastRuns.find((run) => run.kind === 'preview') ?? null;
  const running = latest?.status === 'running';
  const lastSucceeded = lastPreviewRun(config);
  const stale = lastSucceeded !== null && isPreviewStale(detail, config, lastSucceeded);

  return (
    <div className="flex w-full max-w-[880px] flex-col gap-6">
      <PageSection
        title="Aperçu de l’opération"
        description={
          config.state === 'draft'
            ? 'Calculé sur les renouvellements déjà prévus chez Recharge, comme si l’opération était activée maintenant. Rien n’est écrit chez Recharge.'
            : 'Calculé sur les renouvellements déjà prévus chez Recharge, avec les réglages de l’opération. Rien n’est écrit chez Recharge.'
        }
        actions={
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onStart();
            }}
            isLoading={isStarting}
            disabled={running}
          >
            <Calculator aria-hidden="true" />
            {lastSucceeded === null ? 'Calculer l’aperçu' : 'Recalculer'}
          </Button>
        }
      >
        {/* Seule région annoncée : l'état du calcul, pas les chiffres relus toutes les 2 s. */}
        <div role="status">
          {running ? (
            <p className="mb-4 flex items-center gap-2 text-sm">
              <Loader2 className="size-4 animate-spin text-muted-foreground" aria-hidden="true" />
              Calcul en cours… (≈ 1 requête Recharge par seconde)
            </p>
          ) : latest?.status === 'failed' ? (
            <p className="mb-4 text-sm text-status-red-fg">{`Aperçu en échec : ${latest.error ?? 'relancez-le.'}`}</p>
          ) : null}
        </div>
        <div className="flex flex-col gap-4">
          {startError === null ? null : (
            <p role="alert" className="text-sm text-status-red-fg">
              {`Calcul impossible : ${startError}`}
            </p>
          )}
          {lastSucceeded?.preview ? (
            <>
              <p className="text-xs tabular-nums text-muted-foreground">
                Aperçu du {formatParisDateTime(new Date(lastSucceeded.finishedAt ?? lastSucceeded.startedAt))}
              </p>
              {stale && !running ? (
                <p className="flex items-start gap-2 rounded-md border border-status-orange-border bg-status-orange-bg px-3 py-2 text-sm text-status-orange-fg">
                  <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                  Les réglages ont changé depuis ce calcul : recalculez l’aperçu pour des chiffres à jour.
                </p>
              ) : null}
              <PreviewResult preview={lastSucceeded.preview} />
            </>
          ) : running ? null : (
            <p className="text-sm text-muted-foreground">
              Calculez l’aperçu pour voir la remise de chaque client : Flux relit les renouvellements prévus chez Recharge, sans rien y écrire.
            </p>
          )}
        </div>
      </PageSection>
      <CustomerPreview operationId={detail.id} />
    </div>
  );
}
